import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import type { EvalRunListItem } from "~/services/evals";
import { BrokenFlakyCasesCard } from "./BrokenFlakyCasesCard";
import { EvalKpiRow } from "./EvalKpiRow";
import { ChartSkeleton } from "./EvalSkeletons";
import { RecentRunsCard } from "./RecentRunsCard";
import { RunTimeTrend } from "./RunTimeTrend";
import { ScoreTrend } from "./ScoreTrend";
import type { BrokenOrFlakyCase } from "./selectors/cases";
import type { EvalKpis } from "./selectors/kpis";
import type { RunTimePoint, TrendPoint } from "./selectors/runs";
import type { RunAgentLabels } from "./useEvalApi";

interface EvalsOverviewProps {
  kpis: EvalKpis;
  scoreTrend: TrendPoint[];
  runTimeTrend: RunTimePoint[];
  brokenAndFlaky: BrokenOrFlakyCase[];
  /** Newest first. */
  recentRuns: EvalRunListItem[];
  agentLabels: RunAgentLabels;
  isLoading: boolean;
  onSelectRun: (runId: string) => void;
  onSelectCase: (caseId: string, runId?: string) => void;
}

/**
 * The evals landing page: how the suite is doing, then where to look. The headline
 * figures and the two trends say whether it is healthy; the two cards below name
 * the cases and runs to open. Opening a row shows that case or run in a drawer;
 * the full tables live on their own pages, which the cards link to.
 */
export const EvalsOverview = ({
  kpis,
  scoreTrend,
  runTimeTrend,
  brokenAndFlaky,
  recentRuns,
  agentLabels,
  isLoading,
  onSelectRun,
  onSelectCase,
}: EvalsOverviewProps) => (
  <div className="flex flex-col gap-4">
    <EvalKpiRow kpis={kpis} isLoading={isLoading} />

    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Score by run</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? <ChartSkeleton /> : <ScoreTrend points={scoreTrend} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-baseline justify-between gap-3 pb-2">
          <CardTitle className="text-base font-semibold">Run time trend</CardTitle>
          <span className="text-xs whitespace-nowrap text-muted-foreground">Minutes per run</span>
        </CardHeader>
        <CardContent>
          {isLoading ? <ChartSkeleton /> : <RunTimeTrend points={runTimeTrend} />}
        </CardContent>
      </Card>
    </div>

    <BrokenFlakyCasesCard
      cases={brokenAndFlaky}
      isLoading={isLoading}
      onSelectCase={onSelectCase}
    />
    <RecentRunsCard
      runs={recentRuns}
      agentLabels={agentLabels}
      isLoading={isLoading}
      onSelectRun={onSelectRun}
    />
  </div>
);
